<template>
    <div class="max-w-screen-2xl">
        <internal-header v-if="!pdf_view" heading="Manage Productions" :show-button="true" button-text="Add Productions" :button-link="createClick"/>
        <div v-if="!pdf_view" class="tw-flex tw-gap-3 tw-mt-2">
            <div class="tw-w-1/3">
                <v-text-field
                    v-model="search"
                    label="Search Productions"
                    outlined
                    dense
                    append-icon="mdi-magnify"
                    clearable
                ></v-text-field>
            </div>
            <date-picker
                v-model="filters.date_start"
                :isRange="true"
                dense
                color="primary"
                label="Date Start"
                display-label="Date Start"
                ref="dateStartDatePicker"
                hide-details
                clearable
            />
            <date-picker
                v-model="filters.date_wrap"
                :isRange="true"
                dense
                color="primary"
                label="Date Wrap"
                display-label="Date Wrap"
                ref="dateWrapDatePicker"
                hide-details
                clearable
            />
            <v-autocomplete
                v-model="filters['addresses.province_id']"
                :items="valuelist.PROVINCES"
                multiple
                label="Province"
                outlined
                dense
                clearable
            ></v-autocomplete>
            <v-autocomplete
                v-model="filters.contract_id"
                :items="valuelist.CONTRACTS"
                multiple
                label="Contract Type"
                outlined
                dense
                clearable
            ></v-autocomplete>
            <v-btn @click="exportAsPdf" :loading="printLoading" class="main-btn">
                Print
            </v-btn>
            <v-btn @click="requestExportExecution" :loading="exportLoading" class="main-btn">
                Export
            </v-btn>
        </div>
        <smart-table
            id="productions_list"
            endpoint="productions.list"
            :items="items"
            :hide-default-footer="pdf_view"
            :headers="headers"
            :search="search"
            :multi-sort="true"
            :items-per-page="getDefaultItemsPerPage()"
            :display_fields="displayed_column_names"
            :footer-props="{
                'items-per-page-options': getDefaultItemsPerPageOptions()
            }"
            flat
            class="row-pointer"
            @click:row="showProduction"
            ref="productions_list"
        >
            <template v-slot:item.name="{ isMobile, item, header, value, index}">
                {{ item.name}}
            </template>
            <template v-slot:item.alternate_name="{ isMobile, item, header, value, index}">
                {{ item.alternate_name }}
            </template>
            <template v-slot:item.date_start="{ isMobile, item, header, value, index}">
                {{ item.date_start}}
            </template>
            <template v-slot:item.date_wrap="{ isMobile, item, header, value, index}">
                {{ item.date_wrap}}
            </template>
            <template v-slot:item.addresses.province_id="{ isMobile, item, header, value, index}">
                {{ generateList(item.addresses, 'province_id', valuelist.PROVINCES) }}
            </template>
            <template v-slot:item.contract_id="{ isMobile, item, header, value, index}">
                {{ findValuelist(valuelist.CONTRACTS, item.contract_id).text }}
            </template>
        </smart-table>

    </div>
</template>

<script>
    import SmartTable from "@/Generic/SmartTable";
    import AppLayout from "@/Layouts/AppLayout.vue";
    import InternalHeader from "@/Generic/InternalHeader";
    import DatePicker from "@/Generic/DatePicker";
    // import ExportMembersModal from "@/Modals/ExportMembersModal.vue";

    export default {
        name: "ProductionsIndex",
        layout: (h, page) => {
            return h(AppLayout, [
                page
            ])
        },
        components: {
            SmartTable,
            InternalHeader,
            DatePicker,
        },
        remember: {
            data: [
                'items'
            ]
        },
        props: {
            'constants': {
                type: Object,
                required: false,
                default: function () {
                    return undefined
                }
            },
            'valuelists': {
                type: Object,
                required: false,
                default: function () {
                    return undefined
                }
            },
            'items': {          // Will optionally accept a hardcoded list of items instead of getting smart table to fetch them
                type: Array,
                required: false,
                default: function () {
                    return undefined
                }
            },
            'pdf_view': {          // Will optionally accept a hardcoded list of items instead of getting smart table to fetch them
                type: Boolean,
                required: false,
                default: function () {
                    return false
                }
            },
        },
        data() {
            return {
                search: null,
                constant: this.constants,
                valuelist: this.valuelists,
                filters: {
                    name: undefined,
                    alternate_name: undefined,
                    date_start: undefined,
                    date_wrap: undefined,
                    'addresses.province_id': undefined,
                    contract_id: undefined,
                },
                exportLoading: false,
                printLoading: false,
            }
        },
        computed: {
            headers() {
                return [
                    {value: 'id',                           text: 'ID',                             align: `end`,   sortable: true,   filterable: false,   },     // ` d-none` Makes the column disappear (display:none)
                    {value: 'name',                         text: 'Name',                           align: `start`, sortable: true,   filterable: true,   search: this?.filters?.name, optional: false, },     // ` d-none` Makes the column disappear (display:none)
                    {value: 'alternate_name',               text: 'Alternate Name',                 align: `start`, sortable: false,  filterable: true,   search: this?.filters?.alternate_name, optional: false, },
                    {value: 'date_start',                   text: 'Start Date',                     align: `start`, sortable: true,   filterable: true,   search: this?.filters?.date_start, optional: false, },
                    {value: 'date_wrap',                    text: 'Wrap Date',                      align: `start`, sortable: true,   filterable: true,   search: this?.filters?.date_wrap, optional: false, },
                    {value: 'addresses.province_id',        text: 'Province',                       align: `start`, sortable: false,  filterable: true,   search: this?.filters?.["addresses.province_id"], optional: false, },
                    {value: 'contract_id',                  text: 'Contract Type',                  align: `start`, sortable: true,   filterable: true,   search: this?.filters?.contract_id, optional: false, },
                ]
            },
            displayed_column_names() {
                let headers_to_display = this.headers.filter(header => this.should_column_be_displayed(header.value))
                return headers_to_display.map(header => header.value)
            },
        },
        methods: {
            should_column_be_displayed(column) {
                let required_headers = ['id', 'name', 'alternate_name', 'date_start', 'date_wrap', 'addresses.province_id', 'contract_id']
                return required_headers.includes(column)
            },
            generateList(object, key, valuelist) {
                let list = []
                if (valuelist === undefined) {
                    list = object.map(obj => eval(`obj.${key}`))
                } else {
                    list = object.map(obj => this.findValuelist(valuelist, eval(`obj.${key}`)).text)
                }
                let listWithoutDuplicates = [...new Set(list)]
                return listWithoutDuplicates.join(', ')
            },
            generateFiltersWithSearch() {
                let filtersWithSearch = structuredClone(this.filters)
                filtersWithSearch.search = this.search
                return filtersWithSearch
            },
            handleButtonLink() {
                // Define your click event logic here
                console.log('Button clicked in the parent component');
            },
            async requestExportExecution() {
                let data = {filter: this.generateFiltersWithSearch()}
                axios({
                    url: route('productions.export'),
                    method: 'post',
                    data: data,
                    onUploadProgress: (evt) => {
                        this.exportLoading = !this.exportLoading
                    },
                    onDownloadProgress: (evt) => {
                        this.exportLoading = !this.exportLoading
                    },
                })
                    .then(response => {
                        if (response.data.success) {
                            this.$root.snackbar.success(`You will receive an email to ${this.$page.user?.email} with the export once it is done.`)
                        } else {
                            throw (response.data.error)
                        }
                    })
                    .catch((error) => {
                        this.$root.snackbar.error(error.response.data.message)
                        this.exportLoading = false
                    })

                this.exportLoading = false
            },
            findValuelist(list, id) {
                if (list && id) {
                    return list.find(obj => obj.value === id)
                } else {
                    return ""
                }
            },
            getDefaultItemsPerPage() {
                return this.pdf_view ? -1 : 20
            },
            getDefaultItemsPerPageOptions() {
                return this.pdf_view ? [-1] : [5, 10, 20, 30]
            },
            async exportAsPdf() {
                let payload = {
                    filter: this.generateFiltersWithSearch()
                }

                axios({
                    url: route('productions.print'),
                    method: 'post',
                    data: payload,
                })
                    .then(response => {
                        if (response.data.success) {
                            this.$root.snackbar.success(`You will receive an email to ${this.$page.user?.email} with the print once it is done.`)
                        } else {
                            throw (response.data.error)
                        }
                    })
                    .catch((error) => {
                        this.$root.snackbar.error(error.response.data.message)
                    })
            },
            showProduction(production) {
                this.$inertia.visit( route('productions.show', production.id) )
            },
            createClick() {
                this.$inertia.visit(route('productions.create'))
            },
        }
    }
</script>
<style>
.row-pointer tbody tr {
    cursor: pointer;
}
</style>
